import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { sitesApi } from '@/api/sites';
import { queryKeys } from '@/lib/queryKeys';
import type { BootstrapCredential, CloneSiteRequest, CreateFilesSiteRequest, CreateSiteRequest, EnqueueResult, JobDetail, MigrateSiteRequest, ObserveSiteRequest, RelocateSiteRequest, SiteCommandRequest, SitePlatformRequest } from '@/types/api';
import type { TagSelection } from '@/types/tags';
import { useApiMutation, type MutationCallbacks } from '@/hooks/useApiMutation';
import { idempotencyKey } from '@/utils/idempotency';

export interface SiteFilterSelection {
  readonly search: string;
  readonly status: string;
  readonly tier: string;
  readonly tags: TagSelection;
}

/**
 * The site list, paged. The previous result stays on screen while the next one
 * loads, so narrowing a filter does not blank the table between keystrokes.
 * Asked again until `pollUntil` after a sync is queued: the worker reads 2d2s
 * in the background and there is no other way to learn when it is done.
 */
export function useSites({ search, status, tier, tags }: SiteFilterSelection, pollUntil = 0) {
  return useInfiniteQuery({
    queryKey: queryKeys.sites.list(search, status, tier, tags),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      sitesApi.list({
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
        ...(tier ? { tier } : {}),
        ...(tags.untagged ? { untagged: true } : { tagIds: tags.tagIds }),
        cursor: pageParam,
      }),
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
    refetchInterval: () => (Date.now() < pollUntil ? 3000 : false),
  });
}

/** The sites on one server (ADR-0058), paged. */
export function useServerSites(serverId: string) {
  return useInfiniteQuery({
    queryKey: queryKeys.sites.server(serverId),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => sitesApi.list({ serverId, cursor: pageParam }),
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 30_000,
  });
}

/** The 2d2s site tag, if one is configured. */
export function useSiteSync() {
  return useQuery({ queryKey: queryKeys.siteSync, queryFn: sitesApi.sync, staleTime: Infinity });
}

export function useSyncTaggedSites(callbacks?: MutationCallbacks<EnqueueResult, void>) {
  return useApiMutation(() => sitesApi.syncTagged(idempotencyKey('sites.sync')), () => [], callbacks);
}

/** States in which a site changes by itself, so its page asks again on its own. */
const MOVING = new Set(['provisioning', 'destroying']);

export function useSite(siteId: string) {
  return useQuery({
    queryKey: queryKeys.site(siteId),
    queryFn: () => sitesApi.get(siteId),
    enabled: siteId !== '',
    // Asked again while it changes by itself: a site being made or removed, a picture on its way.
    refetchInterval: (query) => (MOVING.has(query.state.data?.status ?? '') ? 3000 : query.state.data?.preview.requested ? 15_000 : false),
  });
}

export function useSiteBackups(siteId: string) {
  return useInfiniteQuery({
    queryKey: queryKeys.siteBackups(siteId),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => sitesApi.backups(siteId, pageParam),
    getNextPageParam: (last) => last.nextCursor,
    enabled: siteId !== '',
  });
}

/** Who did what to the site and how it ended, from the audit trail. */
export function useSiteActivity(siteId: string) {
  return useInfiniteQuery({
    queryKey: queryKeys.siteActivity(siteId),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => sitesApi.activity(siteId, pageParam),
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 15_000,
  });
}

/**
 * A job finishing changes the site — a new backup, a new health reading — so
 * the site, its backups and its history are read again whenever the number of
 * finished jobs moves.
 */
export function useRefreshOnFinishedJobs(siteId: string, jobs: readonly JobDetail[]): void {
  const client = useQueryClient();
  const finished = jobs.filter((job) => job.state === 'completed' || job.state === 'failed').length;
  useEffect(() => {
    if (finished > 0) {
      void client.invalidateQueries({ queryKey: queryKeys.site(siteId) });
      void client.invalidateQueries({ queryKey: queryKeys.siteBackups(siteId) });
      void client.invalidateQueries({ queryKey: queryKeys.jobs.siteHistory(siteId) });
    }
  }, [finished, siteId, client]);
}

type Queued = EnqueueResult & { siteId: string };

export function useCreateSite(callbacks?: MutationCallbacks<Queued, { input: CreateSiteRequest; key: string }>) {
  return useApiMutation(({ input, key }) => sitesApi.create(input, key), () => [queryKeys.sites.all, queryKeys.jobs.all], callbacks);
}

/**
 * A new site of files (ADR-0044): its archive sent first, when there is one,
 * then the site asked for. The upload's id is fixed per attempt, as the
 * request's key is, so a retry does not send the archive twice.
 */
export function useCreateFilesSite(
  callbacks?: MutationCallbacks<Queued, { input: Omit<CreateFilesSiteRequest, 'uploadId'>; archive: File | null; uploadId: string; key: string }>,
) {
  return useApiMutation(
    async ({ input, archive, uploadId, key }) => {
      if (archive) {
        const digest = await crypto.subtle.digest('SHA-256', await archive.arrayBuffer());
        const sha256 = [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
        await sitesApi.uploadArchive(uploadId, archive, sha256).catch((error: unknown) => {
          // Received on an earlier attempt: the site can be asked for.
          if (!(error instanceof Error && /already been received/.test(error.message))) throw error;
        });
      }
      return sitesApi.createFiles({ ...input, ...(archive ? { uploadId } : {}) }, key);
    },
    () => [queryKeys.sites.all, queryKeys.jobs.all],
    callbacks,
  );
}

export function useCloneSite(
  siteId: string,
  callbacks?: MutationCallbacks<Queued, { input: CloneSiteRequest; key: string }>,
) {
  return useApiMutation(
    ({ input, key }) => sitesApi.clone(siteId, input, key),
    () => [queryKeys.sites.all, queryKeys.jobs.all],
    callbacks,
  );
}

export function useMigrateSite(
  siteId: string,
  callbacks?: MutationCallbacks<EnqueueResult, { input: MigrateSiteRequest; key: string }>,
) {
  return useApiMutation(
    ({ input, key }) => sitesApi.migrate(siteId, input, key),
    () => [queryKeys.jobs.all, queryKeys.site(siteId)],
    callbacks,
  );
}

export function useRelocateSite(
  siteId: string,
  callbacks?: MutationCallbacks<EnqueueResult, { input: RelocateSiteRequest; key: string }>,
) {
  return useApiMutation(
    ({ input, key }) => sitesApi.relocate(siteId, input, key),
    () => [queryKeys.jobs.all, queryKeys.site(siteId)],
    callbacks,
  );
}

/** Watching a site the panel does not run (S7). */
export function useSetPlatform(siteId: string, callbacks?: MutationCallbacks<{ ok: boolean }, SitePlatformRequest>) {
  return useApiMutation((input: SitePlatformRequest) => sitesApi.setPlatform(siteId, input), () => [queryKeys.site(siteId), queryKeys.sites.all], callbacks);
}

export function useRequestPreview(siteId: string, callbacks?: MutationCallbacks<{ requested: boolean }, void>) {
  return useApiMutation(() => sitesApi.requestPreview(siteId), () => [queryKeys.site(siteId)], callbacks);
}

export function useDetectPlatform(siteId: string, callbacks?: MutationCallbacks<{ queued: boolean }, void>) {
  return useApiMutation(() => sitesApi.detectPlatform(siteId), () => [queryKeys.site(siteId)], callbacks);
}

export function useObserveSite(callbacks?: MutationCallbacks<{ siteId: string }, ObserveSiteRequest>) {
  return useApiMutation(sitesApi.observe, () => [queryKeys.sites.all], callbacks);
}

export function useUnobserveSite(siteId: string, callbacks?: MutationCallbacks<{ removed: boolean }, string>) {
  return useApiMutation((confirmation) => sitesApi.unobserve(siteId, confirmation), () => [queryKeys.sites.all], callbacks);
}

/** Deploy, restart, backup, restore, destroy, upgrade, domains: each queues a job for the site. */
export function useSiteOperation(
  siteId: string,
  callbacks?: MutationCallbacks<EnqueueResult, { command: SiteCommandRequest; key: string }>,
) {
  return useApiMutation(
    ({ command, key }) => sitesApi.operate(siteId, command, key),
    () => [queryKeys.jobs.all, queryKeys.site(siteId), queryKeys.sites.all],
    callbacks,
  );
}

/** The first administrator's password: shown once, and the site's page stops offering it. */
export function useRevealBootstrapCredential(siteId: string, callbacks?: MutationCallbacks<BootstrapCredential, void>) {
  return useApiMutation(() => sitesApi.revealBootstrapCredential(siteId), () => [queryKeys.site(siteId)], callbacks);
}

export function useSetSiteTags(siteId: string, callbacks?: MutationCallbacks<{ ok: boolean }, string[]>) {
  return useApiMutation(
    (tagIds) => sitesApi.setTags(siteId, tagIds),
    // The tag bar counts sites per tag.
    () => [queryKeys.site(siteId), queryKeys.sites.all, queryKeys.tags],
    callbacks,
  );
}
